import React, { useState, useEffect, useRef } from 'react';
import { roomTypesApi, roomsApi, guestsApi, proofsApi, reservationsApi } from '../../services/api';
import { calcBill, fmt, genId, PAYMENT_TYPES, ID_TYPES, NATIONALITIES, BILLING_TYPES, getTodayStr, getNextDayStr, getMinBackdateStr, BACKDATE_DAYS, validateImageFile, getCurrentTimeStr, formatPhone, formatAadhaar, formatPAN } from '../../utils/billing';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

const STEPS = ['Guest Details', 'Stay Details', 'ID Proof Upload', 'Payment & Confirm'];

// stay.checkInTime is stored/displayed as "hh:mm AM/PM" throughout the app,
// but <input type="time"> only speaks 24h "HH:MM" — these bridge the two so
// the editable field below can reuse the existing string format everywhere
// else without a wider format change.
function timeTo24h(time12) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(time12 || '');
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}
function timeFrom24h(time24) {
  const [hStr, min] = (time24 || '').split(':');
  let h = parseInt(hStr, 10);
  if (isNaN(h) || min === undefined) return '';
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${min} ${ap}`;
}

export default function CheckInPage({ hotel, onSuccess, onNavigate, initialEditId, onBack }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [existingGuest, setExistingGuest] = useState(null);
  const [existingProofs, setExistingProofs] = useState([]);
  const [guestHistory, setGuestHistory] = useState([]);
  const [showGuestHistory, setShowGuestHistory] = useState(false);
  const [proofFiles, setProofFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const fileRef = useRef();

  const [guest, setGuest] = useState({
    salutation: 'Mr.', name: '', gender: 'Male', phone: '', email: '', nationality: 'Indian',
    customerType: 'individual',
    idType: 'Aadhaar Card', idNumber: '',
    address: '', company: '', companyGst: '',
  });

  const [roomTypes, setRoomTypes] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [availability, setAvailability] = useState({});
  const [stay, setStay] = useState({
    checkIn: getTodayStr(),
    checkOut: getNextDayStr(getTodayStr()), checkInTime: getCurrentTimeStr(),
    nights: 1, extraCharges: '0',
    advancePaid: '0', notes: '', extension: false, extensionDate: '',
    billingCycle: '24h',
    roomConfigs: [],
    billingType: 'non-gst'
  });

  useEffect(() => {
    if (hotel && stay.checkIn && stay.checkOut) {
      reservationsApi.checkAvailabilityCounts(hotel.id, stay.checkIn, stay.checkOut)
        .then(counts => setAvailability(counts))
        .catch(err => console.error('Availability fetch error', err));
    }
  }, [hotel, stay.checkIn, stay.checkOut]);

  useEffect(() => {
    if (hotel) {
      roomTypesApi.list(hotel.id).then(types => {
        setRoomTypes(types);
        if (types.length > 0) {
          setStay(s => s.roomConfigs.length === 0 ? {
            ...s, roomConfigs: [
              { id: Date.now(), room: '', roomType: types[0].name, roomRate: String(types[0].base_price), adults: '2', children: '0', roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(types[0].base_price) }
            ]
          } : s);
        }
      }).catch(err => {
        console.error('Room types fetch error', err);
        onSuccess?.('Could not load room types — check your connection and reload.', 'error');
      });
      roomsApi.list(hotel.id).then(rooms => setAllRooms(rooms)).catch(err => {
        console.error('Rooms fetch error', err);
        onSuccess?.('Could not load room availability — check your connection and reload.', 'error');
      });
    }
  }, [hotel, onSuccess]);

  // Item 8: Redirect to Reservation / Pre-booking if 0 vacant rooms available today.
  // Must NOT fire while editing an existing reservation (initialEditId set) — editing
  // doesn't need a fresh vacant room, and this previously force-navigated away mid-edit
  // (discarding whatever the user had already loaded/typed) with a misleading toast,
  // right at the moment a hotel is near full — exactly when edits are most likely.
  useEffect(() => {
    if (hotel && allRooms.length > 0 && !initialEditId) {
      const vacantCount = allRooms.filter(r => r.status === 'vacant').length;
      if (vacantCount === 0) {
        onSuccess?.('No rooms available today for direct check-in. Redirected to Pre-booking section.', 'info');
        if (onNavigate) onNavigate('prebooking');
      }
    }
  }, [hotel, allRooms, onNavigate, onSuccess, initialEditId]);

  // Pre-load a reservation for editing when navigated from Dashboard
  useEffect(() => {
    if (initialEditId && hotel) {
      reservationsApi.get(initialEditId).then(r => {
        if (!r) return;
        // Pre-fill guest state
        setGuest({
          salutation: r.salutation || 'Mr.',
          name: r.guest_name || r.name || '',
          gender: r.gender || 'Male',
          phone: r.phone || r.guest_phone || '',
          email: r.email || '',
          nationality: r.nationality || 'Indian',
          customerType: r.customer_type || 'individual',
          idType: r.id_type || 'Aadhaar Card',
          idNumber: r.id_number || '',
          address: r.address || '',
          company: r.company || '',
          companyGst: r.company_gst || '',
        });
        // Pre-fill stay state
        setStay(prev => ({
          ...prev,
          checkIn: r.check_in || prev.checkIn,
          checkOut: r.check_out || prev.checkOut,
          checkInTime: r.check_in_time || prev.checkInTime,
          nights: r.nights || prev.nights,
          notes: r.notes || '',
          billingType: r.billing_type || 'non-gst',
          roomConfigs: r.room
            ? [{ id: Date.now(), room: r.room, roomType: r.room_type || '', roomRate: String(r.room_rate || ''), adults: String(r.adults || 2), children: String(r.children || 0), roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(r.room_rate || '') }]
            : prev.roomConfigs
        }));
        setStep(0);
      }).catch(err => console.error('Edit load error:', err));
    }
  }, [initialEditId, hotel]);


  const handleRoomConfigChange = (id, changes) => {
    setStay(prev => {
      const nextConfigs = prev.roomConfigs.map(c => {
        if (c.id !== id) return c;
        let next = { ...c, ...changes };
        if ('roomType' in changes || 'adults' in changes || 'children' in changes || 'capacityPreference' in changes || 'offeredPrice' in changes) {
          const typeDef = roomTypes.find(t => t.name === next.roomType);
          if (typeDef) {
            const adults = Number(next.adults) || 1;
            const children = Number(next.children) || 0;
            const maxA = typeDef.maxAdults || 3;
            const baseA = typeDef.baseAdults || typeDef.base_adults || 2;
            const baseC = typeDef.baseChildren || typeDef.base_children || 2;

            // Reset offered price when room type changes
            if ('roomType' in changes) {
              next.offeredPrice = String(typeDef.base_price || 0);
            }

            let rc = 1;
            let eb = 0;

            if (next.capacityPreference === 'exception') {
              rc = 1; eb = 0;
            } else if (next.capacityPreference === 'extra_room' && (adults > baseA || children > baseC)) {
              rc = Math.max(Math.ceil(adults / baseA), children > baseC ? Math.ceil(children / baseC) : 1);
              eb = 0;
            } else {
              rc = adults > maxA ? Math.ceil(adults / maxA) : 1;
              eb = Math.max(0, adults - (rc * baseA));
              if (children > baseC) eb += (children - baseC);
            }
            next.roomsCount = rc;
            next.extraBeds = eb;

            const basePrice = (typeDef.base_price || 0);
            const ebPrice = (typeDef.extra_bed_price || 0);
            const offeredPrice = Number(next.offeredPrice) || basePrice;

            // roomRate = total nightly cost for ALL rooms in this config
            next.roomRate = String(Math.round((rc * offeredPrice + eb * ebPrice) * 100) / 100);
          }
        }
        return next;
      });
      return { ...prev, roomConfigs: nextConfigs };
    });
  };

  const addRoomConfig = () => {
    setStay(prev => ({
      ...prev,
      roomConfigs: [
        ...prev.roomConfigs,
        { id: Date.now() + Math.random(), room: '', roomType: roomTypes[0]?.name || '', roomRate: String(roomTypes[0]?.base_price || ''), adults: '2', children: '0', roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(roomTypes[0]?.base_price || '') }
      ]
    }));
  };

  const removeRoomConfig = (id) => {
    setStay(prev => ({
      ...prev,
      roomConfigs: prev.roomConfigs.filter(c => c.id !== id)
    }));
  };

  const totalRoomRate = stay.roomConfigs.reduce((sum, c) => {
    // Each config's roomRate already represents the full nightly cost for that config.
    return sum + (Number(c.roomRate) || 0);
  }, 0);

  const [paymentOption, setPaymentOption] = useState('advance');

  const mkPayRow = () => ({ id: Date.now() + Math.random(), type: 'cash', last5: '', amount: '', note: '' });
  const [payments, setPayments] = useState([mkPayRow()]);

  // Keep the single-row (common) case effortless: as long as staff hasn't
  // split into multiple payment methods, the one row always mirrors the
  // advance amount above it so nothing extra needs to be typed twice.
  useEffect(() => {
    setPayments(prev => prev.length === 1 ? [{ ...prev[0], amount: stay.advancePaid }] : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stay.advancePaid]);

  const [phoneConflict, setPhoneConflict] = useState(null);

  const searchGuest = async (p) => {
    const raw = typeof p === 'string' ? p : phoneSearch;
    const term = raw.replace(/\D/g, '');
    if (!term) return;
    if (term.length !== 10) {
      onSuccess?.('Please enter a valid 10-digit mobile number.', 'error');
      return;
    }
    const gs = await guestsApi.list(term);
    if (gs.length > 0) {
      const g = gs[0];
      setExistingGuest(g);
      const rawIdType = g.id_type || g.idType || 'Aadhaar Card';
      const rawIdNum = g.id_number || g.idNumber || '';
      setGuest({
        ...guest,
        salutation: g.salutation || 'Mr.',
        name: g.name, gender: g.gender || 'Male', phone: g.phone, email: g.email || '',
        nationality: g.nationality || 'Indian',
        customerType: g.customer_type || g.customerType || 'individual',
        idType: rawIdType,
        idNumber: rawIdType === 'Aadhaar Card' ? formatAadhaar(rawIdNum) : rawIdNum,
        address: g.address || '', company: g.company || '', companyGst: g.company_gst || g.companyGst || '',
      });
      setPhoneConflict(null);
      // Fetch existing proofs
      try {
        const proofs = await proofsApi.list(g.id);
        setExistingProofs(proofs || []);
      } catch (e) {
        console.error('Proofs fetch failed', e);
      }
      // Surface this guest's past stays/bills right here — so staff can see
      // what they paid or which room they usually take without leaving the
      // check-in flow to go look it up separately.
      try {
        const pastRes = await reservationsApi.list(hotel.id, undefined, g.id);
        const completed = (pastRes || [])
          .filter(r => r.status === 'checked-out')
          .sort((a, b) => new Date(b.check_out) - new Date(a.check_out))
          .slice(0, 5);
        setGuestHistory(completed);
      } catch (e) {
        console.error('Guest history fetch failed', e);
        setGuestHistory([]);
      }
    } else {
      setExistingGuest(null);
      setExistingProofs([]);
      setGuestHistory([]);
      setPhoneConflict(null);
    }
  };

  const checkPhoneConflict = async () => {
    if (!guest.phone || guest.phone.length < 5) return null;
    const cleanInput = guest.phone.replace(/[\s.\-()+]/g, '');
    const gs = await guestsApi.list(cleanInput);

    const conflict = gs.find(g => {
      if (existingGuest && g.id === existingGuest.id) return false;
      const cleanExisting = g.phone.replace(/[\s.\-()+]/g, '');
      return cleanExisting === cleanInput && g.name.trim().toLowerCase() !== guest.name.trim().toLowerCase();
    });

    if (conflict) {
      setPhoneConflict(conflict.name);
      return conflict.name;
    }
    setPhoneConflict(null);
    return null;
  };

  const calcNights = (cin, cout) => {
    if (!cin || !cout) return 0;
    const d = (new Date(cout) - new Date(cin)) / 86400000;
    return Math.max(1, Math.round(d));
  };

  const bill = calcBill(
    { roomRate: totalRoomRate, nights: Number(stay.nights) || 1, extraCharges: Number(stay.extraCharges) || 0, advancePaid: Number(stay.advancePaid) || 0 },
    stay.billingType,
    hotel?.gstRate
  );

  const handleProofUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      try {
        await validateImageFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => {
          setProofFiles(prev => [...prev, { name: file.name, data: ev.target.result, type: file.type }]);
        };
        reader.readAsDataURL(file);
      } catch (err) {
        await notify(err.message);
      }
    }
  };

  const removeProof = (idx) => setProofFiles(prev => prev.filter((_, i) => i !== idx));

  const deleteExistingProof = async (id) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Document', body: 'Delete this document forever? You will need to upload a fresh copy if you do.', confirmText: 'Delete', danger: true })) return;
    try {
      await proofsApi.delete(id);
      setExistingProofs(prev => prev.filter(p => p.id !== id));
      onSuccess?.('Document deleted successfully', 'success');
    } catch (e) {
      await notify(e.message);
    }
  };

  const validateRooms = () => {
    const errorMessages = [];
    stay.roomConfigs.forEach((config, idx) => {
      const roomNums = (config.room || '').split(',').map(s => s.trim()).filter(Boolean);
      roomNums.forEach(num => {
        const roomObj = allRooms.find(r => r.number === num);
        if (!roomObj) {
          errorMessages.push(`Room ${num} (Config ${idx + 1}) does not exist in the system.`);
        } else if (roomObj.status === 'cleaning') {
          errorMessages.push(`Room ${num} is currently being cleaned. Please mark it as clean before check-in.`);
        } else if (roomObj.status === 'occupied') {
          errorMessages.push(`Room ${num} is already occupied.`);
        } else if (roomObj.status === 'blocked' || roomObj.status === 'maintenance') {
          errorMessages.push(`Room ${num} is blocked or under maintenance.`);
        }
      });
    });
    return errorMessages;
  };

  const handleSubmit = async () => {
    if (!stay.checkIn || stay.checkIn < getMinBackdateStr()) {
      onSuccess?.(`Check-in date can be back-dated by up to ${BACKDATE_DAYS} days only (earliest ${getMinBackdateStr()}).`, 'error');
      return;
    }
    const errors = validateRooms();
    if (errors.length > 0) {
      onSuccess?.(errors[0], 'error');
      return;
    }
    const advanceTotal = Number(stay.advancePaid) || 0;
    const activePayments = payments.filter(p => Number(p.amount) > 0);
    if (advanceTotal > 0) {
      const splitTotal = activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      if (Math.abs(advanceTotal - splitTotal) > 0.5) {
        onSuccess?.(`Payment split (${fmt(splitTotal)}) doesn't match the advance amount (${fmt(advanceTotal)}).`, 'error');
        return;
      }
      const missingRef = activePayments.find(p => ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) && !p.last5);
      if (missingRef) {
        onSuccess?.('Enter the reference (last 5 digits) for every non-cash payment.', 'error');
        return;
      }
    }
    setLoading(true);
    try {
      // 1. Create/Update guest
      const guestData = { ...guest, hotelId: hotel.id };
      let guestId;
      if (existingGuest) {
        guestData.id = existingGuest.id;
        const gRes = await guestsApi.update(existingGuest.id, guestData);
        guestId = gRes.id;
      } else {
        const gRes = await guestsApi.create(guestData);
        guestId = gRes.id;
      }

      // 2. Create reservation
      const resId = genId('R');
      const nights = calcNights(stay.checkIn, stay.checkOut) || Number(stay.nights) || 1;
      const combinedRoom = stay.roomConfigs.map(c => c.room).filter(Boolean).join(', ');
      const combinedRoomType = stay.roomConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ');
      const roomConfigsPayload = stay.roomConfigs.map(c => {
        const typeDef = roomTypes.find(t => t.name === c.roomType);
        const basePrice = Number(typeDef?.base_price ?? c.basePrice ?? c.offeredPrice ?? c.roomRate ?? 0);
        const offeredPrice = Number(c.offeredPrice ?? c.roomRate ?? 0);
        return {
          room: c.room,
          roomType: c.roomType,
          roomRate: Number(c.roomRate) || 0,
          roomsCount: Number(c.roomsCount) || 1,
          extraBeds: Number(c.extraBeds) || 0,
          capacityPreference: c.capacityPreference || 'extra_bed',
          basePrice,
          offeredPrice
        };
      });

      await reservationsApi.create({
        id: resId, guestId, hotelId: hotel.id,
        room: combinedRoom, roomType: combinedRoomType,
        checkIn: stay.checkIn, checkOut: stay.checkOut,
        checkInTime: stay.checkInTime,
        status: 'checked-in', nights,
        billingCycle: stay.billingCycle,
        roomRate: totalRoomRate,
        extraCharges: Number(stay.extraCharges) || 0,
        totalAmount: bill.total,
        advancePaid: advanceTotal,
        advancePayments: activePayments.map(p => ({ type: p.type, amount: Number(p.amount) || 0, last5: p.last5 || null, note: p.note || null })),
        customerType: guest.customerType,
        company: guest.company,
        companyGst: guest.companyGst,
        billingType: stay.billingType,
        notes: stay.notes,
        extension: stay.extension,
        roomConfigs: roomConfigsPayload
      });

      // 3. Save ID proofs against the newly created reservation
      const proofUploadErrors = [];
      for (const proof of proofFiles) {
        try {
          await proofsApi.create({
            guestId,
            hotelId: hotel.id,
            reservationId: resId,
            type: guest.idType,
            fileName: proof.name,
            data: proof.data
          });
        } catch (err) {
          proofUploadErrors.push(`${proof.name}: ${err.message}`);
        }
      }

      // 4. Record advance payment handled automatically in backend server.js on reservation post if d.advancePaid > 0

      onSuccess?.(
        proofUploadErrors.length > 0
          ? `Guest checked in, but ${proofUploadErrors.length} proof upload(s) failed. ${proofUploadErrors[0]}`
          : 'Guest checked in successfully!',
        proofUploadErrors.length > 0 ? 'error' : 'success'
      );
      setStep(0); setExistingGuest(null); setPhoneSearch(''); setProofFiles([]); setExistingProofs([]); setPreviewFile(null);
      setGuest({ salutation: 'Mr.', name: '', phone: '', gender: 'Male', email: '', nationality: 'Indian', customerType: 'individual', idType: 'Aadhaar Card', idNumber: '', address: '', company: '', companyGst: '' });
      setStay({ checkIn: getTodayStr(), checkOut: getNextDayStr(getTodayStr()), checkInTime: getCurrentTimeStr(), nights: 1, extraCharges: '0', advancePaid: '0', notes: '', extension: false, extensionDate: '', billingCycle: '24h', billingType: 'non-gst', roomConfigs: [{ id: Date.now(), room: '', roomType: roomTypes[0]?.name || '', roomRate: String(roomTypes[0]?.base_price || ''), adults: '2', children: '0', roomsCount: 1, extraBeds: 0, capacityPreference: 'extra_bed', offeredPrice: String(roomTypes[0]?.base_price || '') }] });
      setPayments([mkPayRow()]);
    } catch (err) {
      console.error(err);
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setLoading(false);
  };

  const isCorporate = guest.customerType === 'corporate';

  const renderPreviewModal = () => {
    if (!previewFile) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 3000 }} onClick={() => setPreviewFile(null)}>
        <div className="modal-content" style={{ maxWidth: 800, width: '90%', padding: '10px', position: 'relative', borderRadius: 16 }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-sm btn-ghost" style={{ position: 'absolute', top: -40, right: 0, color: '#fff', fontSize: 16, fontWeight: 700 }} onClick={() => setPreviewFile(null)}>✕ Close Preview</button>
          <div style={{ maxHeight: '80vh', overflow: 'auto', textAlign: 'center' }}>
            {previewFile.type?.startsWith('image/') || (typeof previewFile === 'string' && previewFile.startsWith('data:image/')) || (previewFile.data?.startsWith('data:image/'))
              ? <img src={previewFile.data || previewFile} alt="Full Preview" style={{ maxWidth: '100%', borderRadius: 8 }} />
              : <div style={{ background: '#fff', color: '#333', padding: 40, borderRadius: 12 }}>
                  <h3 style={{ marginBottom: 15 }}>{previewFile.name || 'Document'}</h3>
                  <a href={previewFile.data || previewFile} download={previewFile.name || 'file'} className="btn btn-gold">Download to View Full File</a>
                </div>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderPreviewModal()}

      {/* BACK TO DASHBOARD button — shown when opened from Dashboard edit */}
      {onBack && (
        <div style={{ marginBottom: 16 }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid var(--border)', borderRadius: 8 }}
            onClick={onBack}
          >
            ← Back to Dashboard
          </button>
        </div>
      )}
      {/* STEP INDICATOR */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: 'var(--s1)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 600,
            background: i === step ? 'var(--s3)' : 'transparent',
            color: i === step ? 'var(--accent)' : i < step ? 'var(--success)' : 'var(--muted)',
            borderRight: i < STEPS.length - 1 ? '1px solid var(--border)' : 'none',
            cursor: i < step ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }} onClick={() => i < step && setStep(i)}>
            <span style={{ width: 18, height: 18, borderRadius: '50%', background: i < step ? 'var(--success)' : i === step ? 'var(--accent)' : 'var(--s3)', color: '#0b0c0f', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {i < step ? '✓' : i + 1}
            </span>
            {s}
          </div>
        ))}
      </div>

      {/* STEP 0: GUEST DETAILS */}
      {step === 0 && (
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Step 1 — Guest Details</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {/* Phone search */}
            <div style={{ background: 'var(--s2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 10 }}>🔍 Search Existing Guest</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  className="field input" style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font-b)', fontSize: 13, outline: 'none' }}
                  placeholder="Enter 10-digit mobile number to search..." value={phoneSearch}
                  maxLength={10}
                  onChange={e => setPhoneSearch(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  onKeyDown={e => e.key === 'Enter' && searchGuest()}
                />
                <button className="btn btn-teal" onClick={() => searchGuest()}>Search</button>
              </div>
              {existingGuest && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(109,191,148,0.1)', border: '1px solid rgba(109,191,148,0.2)', borderRadius: 8, fontSize: 12 }}>
                  ✅ Existing guest found: <strong>{existingGuest.name}</strong> — details pre-filled
                </div>
              )}
              {existingGuest && guestHistory.length > 0 && (
                <div style={{ marginTop: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div onClick={() => setShowGuestHistory(v => !v)} style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>🧾 Past Stays & Bills ({guestHistory.length})</span>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{showGuestHistory ? '▲ Hide' : '▼ Show'}</span>
                  </div>
                  {showGuestHistory && (
                    <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {guestHistory.map(r => (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, background: 'var(--s1)', padding: '8px 10px', borderRadius: 6 }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>Room {r.room} · {r.room_type}</div>
                            <div style={{ fontSize: 10, color: 'var(--muted)' }}>{r.check_in} → {r.check_out} · {r.nights} night{r.nights > 1 ? 's' : ''}</div>
                          </div>
                          <div style={{ fontWeight: 700, color: 'var(--success)', whiteSpace: 'nowrap' }}>{fmt(r.total_amount)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-row-3" style={{ marginTop: 14 }}>
              <div className="field">
                <label>Salutation</label>
                <select value={guest.salutation} onChange={e => {
                  const s = e.target.value;
                  const g = (s === 'Ms.' || s === 'Mrs.') ? 'Female' : 'Male';
                  setGuest(f => ({ ...f, salutation: s, gender: g }));
                }}>
                  <option>Mr.</option>
                  <option>Ms.</option>
                  <option>Mrs.</option>
                  <option>Dr.</option>
                </select>
              </div>
              <div className="field" style={{ flex: 2 }}>
                <label>Full Name *</label>
                <input value={guest.name} onChange={e => setGuest(f => ({ ...f, name: e.target.value }))} placeholder="Full name as on ID" />
              </div>
              <div className="field">
                <label>Gender</label>
                <select value={guest.gender} onChange={e => setGuest(f => ({ ...f, gender: e.target.value }))}>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div className="form-row-2" style={{ marginTop: 14 }}>
              <div className="field"><label>Phone *</label><input value={guest.phone} onChange={e => setGuest(f => ({ ...f, phone: formatPhone(e.target.value) }))} placeholder="10-digit mobile" /></div>
              <div className="field"><label>Email</label><input value={guest.email} onChange={e => setGuest(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" /></div>
            </div>
            <div className="form-row-2" style={{ marginTop: 14 }}>
              <div className="field"><label>Nationality</label>
                <select value={guest.nationality} onChange={e => {
                  const val = e.target.value;
                  setGuest(f => ({
                    ...f,
                    nationality: val,
                    idType: val !== 'Indian' ? 'Passport' : f.idType
                  }));
                }}>
                  {NATIONALITIES.map(n => <option key={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <label>Customer Type *</label>
              <select value={guest.customerType} onChange={e => {
                const type = e.target.value;
                setGuest(f => ({ ...f, customerType: type }));
                // Auto-switch billing type
                setStay(s => ({ ...s, billingType: type === 'corporate' ? 'gst' : 'non-gst' }));
              }}>
                <option value="individual">Individual (No GST)</option>
                <option value="corporate">Corporate (GST applicable)</option>
              </select>
            </div>
            {isCorporate && (
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field"><label>Company Name *</label><input value={guest.company} onChange={e => setGuest(f => ({ ...f, company: e.target.value }))} placeholder="Company Pvt Ltd" /></div>
                <div className="field"><label>Company GST Number *</label><input value={guest.companyGst} onChange={e => setGuest(f => ({ ...f, companyGst: e.target.value.toUpperCase() }))} placeholder="27AABCT1234L1ZN" /></div>
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <div className="field"><label>Address</label><textarea value={guest.address} onChange={e => setGuest(f => ({ ...f, address: e.target.value }))} placeholder="Full residential/company address" /></div>
            </div>
            <div className="form-row-2" style={{ marginTop: 14 }}>
              <div className="field"><label>ID Type *</label>
                <select value={guest.idType} onChange={e => setGuest(f => ({ ...f, idType: e.target.value }))}>
                  {ID_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="field"><label>ID Number *</label>
                <input
                  value={guest.idNumber}
                  onChange={e => {
                    const val = e.target.value;
                    let formatted = val;
                    if (guest.idType === 'Aadhaar Card') formatted = formatAadhaar(val);
                    else if (guest.idType === 'PAN Card') formatted = formatPAN(val);
                    setGuest(f => ({ ...f, idNumber: formatted }));
                  }}
                  maxLength={guest.idType === 'Aadhaar Card' ? 14 : undefined}
                  placeholder="ID document number"
                />
              </div>
            </div>
          </div>
          <div style={{ padding: '0 24px 20px' }}>
            {phoneConflict && (
              <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8, padding: '12px 16px', color: '#c53030', fontSize: 13, marginBottom: 16 }}>
                ⚠️ <strong>Phone Number Conflict:</strong> This mobile number belongs to <strong>"{phoneConflict}"</strong> in our records. You cannot use it for "{guest.name}".
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-gold"
                onClick={async () => {
                  const conflict = await checkPhoneConflict();
                  if (!conflict) setStep(1);
                  else onSuccess?.(`Phone already registered to "${conflict}".`, 'error');
                }}
                disabled={!guest.name || !guest.phone || !guest.idNumber}
              >
                Next: Stay Details →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 1: STAY DETAILS */}
      {step === 1 && (
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Step 2 — Stay Details</span>
            <span className="panel-sub">{guest.name} · {isCorporate ? '🏢 Corporate' : '👤 Individual'}</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <div className="form-row-2">
              <div className="field"><label>Check-in Date * <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(missed entry? up to {BACKDATE_DAYS} days back)</span></label><input type="date" min={getMinBackdateStr()} value={stay.checkIn} onChange={e => { const n = calcNights(e.target.value, stay.checkOut); setStay(f => ({ ...f, checkIn: e.target.value, checkOut: (!f.checkOut || e.target.value > f.checkOut) ? '' : f.checkOut, nights: n || f.nights })); }} /></div>
              <div className="field"><label>Arrival Time *</label>
                <input type="time" value={timeTo24h(stay.checkInTime)} onChange={e => setStay(f => ({ ...f, checkInTime: timeFrom24h(e.target.value) || f.checkInTime }))} />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Defaults to now — adjust if you're entering this check-in after the guest actually arrived.</div>
              </div>
            </div>
            <div className="form-row-3" style={{ marginTop: 14, marginBottom: 24 }}>
              <div className="field">
                <label>Billing Cycle *</label>
                <select value={stay.billingCycle} onChange={e => setStay(f => ({ ...f, billingCycle: e.target.value }))}>
                  <option value="24h">24 Hours System</option>
                  <option value="12h">12 Hours System</option>
                </select>
              </div>
              <div className="field"><label>Check-out Date *</label><input type="date" min={stay.checkIn || getMinBackdateStr()} value={stay.checkOut} onChange={e => { const n = calcNights(stay.checkIn, e.target.value); setStay(f => ({ ...f, checkOut: e.target.value, nights: n || f.nights })); }} /></div>
              <div className="field"><label>{stay.billingCycle === '12h' ? 'No. of 12h Slots' : 'No. of Nights'}</label><input type="number" min="1" value={stay.nights} onChange={e => setStay(f => ({ ...f, nights: e.target.value }))} /></div>
            </div>

            {stay.roomConfigs.map((config, index) => {
              const otherConfigsSelected = stay.roomConfigs
                .filter(c => c.id !== config.id)
                .flatMap(c => (c.room || '').split(',').map(s => s.trim()))
                .filter(Boolean);

              return (
              <div key={config.id} style={{ background: 'var(--s1)', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>Accommodation {index + 1}</div>
                  {stay.roomConfigs.length > 1 && (
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)', padding: '4px 8px' }} onClick={() => removeRoomConfig(config.id)}>Remove</button>
                  )}
                </div>
                <div className="form-row-2">
                  <div className="field"><label>Total Adults *</label><input type="number" min="1" value={config.adults} onChange={e => handleRoomConfigChange(config.id, { adults: e.target.value })} /></div>
                  <div className="field"><label>Total Children</label><input type="number" min="0" value={config.children} onChange={e => handleRoomConfigChange(config.id, { children: e.target.value })} /></div>
                </div>
                {(Number(config.adults) > (roomTypes.find(t => t.name === config.roomType)?.baseAdults || 2) || Number(config.children) > (roomTypes.find(t => t.name === config.roomType)?.baseChildren || 2)) && (
                  <div className="field" style={{ marginTop: 14 }}>
                    <label>Extra Pax Accommodation Preference</label>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name={`capPref-${config.id}`} checked={config.capacityPreference === 'extra_bed'} onChange={() => handleRoomConfigChange(config.id, { capacityPreference: 'extra_bed' })} />
                        Add Extra Bed(s)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name={`capPref-${config.id}`} checked={config.capacityPreference === 'extra_room'} onChange={() => handleRoomConfigChange(config.id, { capacityPreference: 'extra_room' })} />
                        Book Extra Room(s)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input type="radio" name={`capPref-${config.id}`} checked={config.capacityPreference === 'exception'} onChange={() => handleRoomConfigChange(config.id, { capacityPreference: 'exception' })} />
                        Exception (No Extra Charge)
                      </label>
                    </div>
                  </div>
                )}
                {(config.roomsCount > 1 || config.extraBeds > 0) && (
                  <div style={{ padding: '8px 12px', background: 'var(--s2)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontSize: 13, marginTop: 10, marginBottom: 4 }}>
                    🛏️ Allocation: <strong>{config.roomsCount} Room(s)</strong> + <strong>{config.extraBeds} Extra Bed(s)</strong>
                  </div>
                )}
                <div className="form-row-3" style={{ marginTop: 14 }}>
                  <div className="field"><label>Room Type</label>
                    <select value={config.roomType} onChange={e => handleRoomConfigChange(config.id, { roomType: e.target.value, room: '' })}>
                      {roomTypes.map(t => {
                        const avail = availability[t.name] !== undefined ? availability[t.name] : (Number(t.total_rooms) || 0);
                        return <option key={t.id} value={t.name}>{t.name} ({avail} Left)</option>
                      })}
                      {roomTypes.length === 0 && <option value="">No Types Configured</option>}
                    </select>
                  </div>
                  <div className="field">
                    <label>Room Number(s) * {config.roomsCount > 1 && `(Select ${config.roomsCount})`}</label>
                    {config.roomsCount <= 1 ? (
                      <select
                        value={config.room}
                        onChange={e => handleRoomConfigChange(config.id, { room: e.target.value })}
                      >
                        <option value="">-- Select Vacant Room --</option>
                        {allRooms
                          .filter(r => r.room_type?.toLowerCase() === config.roomType?.toLowerCase() && (r.status === 'vacant' || r.status === 'cleaning') && !otherConfigsSelected.includes(r.number))
                          .map(r => <option key={r.id} value={r.number}>{r.number} {r.status === 'cleaning' ? '(Cleaning)' : ''}</option>)
                        }
                      </select>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <input
                          readOnly
                          value={config.room}
                          placeholder={`Pick ${config.roomsCount} rooms...`}
                          onClick={(e) => {
                            const el = e.target.nextSibling;
                            if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                          }}
                        />
                        <div className="dropdown-multi" style={{ display: 'none', position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 150, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', padding: 8 }}>
                          {allRooms
                            .filter(r => r.room_type?.toLowerCase() === config.roomType?.toLowerCase() && (r.status === 'vacant' || r.status === 'cleaning') && !otherConfigsSelected.includes(r.number))
                            .map(r => (
                              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={(config.room || '').split(',').map(s => s.trim()).includes(r.number)}
                                  onChange={async e => {
                                    const current = (config.room || '').split(',').map(s => s.trim()).filter(Boolean);
                                    let next;
                                    if (e.target.checked) {
                                      if (current.length >= config.roomsCount) { await notify(`You can only select ${config.roomsCount} rooms for this configuration.`); return; }
                                      next = [...current, r.number];
                                    } else {
                                      next = current.filter(n => n !== r.number);
                                    }
                                    handleRoomConfigChange(config.id, { room: next.join(', ') });
                                  }}
                                />
                                {r.number} {r.status === 'cleaning' ? <span style={{ color: 'var(--danger)', fontSize: 11 }}>(Cleaning)</span> : ''}
                              </label>
                            ))
                          }
                          {allRooms.filter(r => r.room_type === config.roomType && (r.status === 'vacant' || r.status === 'cleaning') && !otherConfigsSelected.includes(r.number)).length === 0 && (
                            <div style={{ padding: 8, fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>No available rooms</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-row-3" style={{ marginTop: 14 }}>
                  {(() => {
                    const typeDef = roomTypes.find(t => t.name === config.roomType);
                    const basePrice = typeDef ? (typeDef.base_price || 0) : 0;
                    const gstRate = Number(hotel?.gstRate ?? 5);
                    return (
                      <>
                        <div className="field"><label>Standard Base Rate / Room</label><input type="number" disabled value={basePrice} /></div>
                        <div className="field">
                          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{config.priceIncludesGst ? 'Offered Price, GST-incl. (₹) *' : 'Offered Price / Room (₹) *'}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 400, color: 'var(--muted)', cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!config.priceIncludesGst} onChange={e => {
                                const checked = e.target.checked;
                                if (checked) {
                                  const inclusiveVal = Number(config.offeredPrice) || basePrice;
                                  const exclusive = gstRate > 0 ? Math.round((inclusiveVal / (1 + gstRate / 100)) * 100) / 100 : inclusiveVal;
                                  handleRoomConfigChange(config.id, { priceIncludesGst: true, inclusivePrice: String(inclusiveVal), offeredPrice: String(exclusive) });
                                  setStay(f => ({ ...f, billingType: 'gst' }));
                                } else {
                                  handleRoomConfigChange(config.id, { priceIncludesGst: false });
                                }
                              }} />
                              Includes GST
                            </span>
                          </label>
                          {config.priceIncludesGst ? (
                            <input type="number" value={config.inclusivePrice ?? ''} onChange={e => {
                              const inclusiveVal = Number(e.target.value) || 0;
                              const exclusive = gstRate > 0 ? Math.round((inclusiveVal / (1 + gstRate / 100)) * 100) / 100 : inclusiveVal;
                              handleRoomConfigChange(config.id, { inclusivePrice: e.target.value, offeredPrice: String(exclusive) });
                            }} placeholder="e.g. 2200" style={{ fontWeight: 700, color: 'var(--teal)', border: '1px solid var(--teal)', outline: 'none' }} />
                          ) : (
                            <input type="number" value={config.offeredPrice !== undefined ? config.offeredPrice : basePrice} onChange={e => handleRoomConfigChange(config.id, { offeredPrice: e.target.value })} style={{ fontWeight: 700, color: 'var(--teal)', border: '1px solid var(--teal)', outline: 'none' }} />
                          )}
                          {config.priceIncludesGst && (
                            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                              = {fmt(config.offeredPrice)} room + {fmt((Number(config.inclusivePrice) || 0) - (Number(config.offeredPrice) || 0))} GST @ {gstRate}% — make sure "Official GST Booking" is selected below
                            </div>
                          )}
                        </div>
                        <div className="field"><label>Final Config Cost / Night (₹)</label><input type="number" disabled value={config.roomRate} style={{ fontWeight: 700, color: 'var(--success)' }} /></div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )})}

            <button className="btn btn-ghost" style={{ width: '100%', border: '1px dashed var(--border2)', color: 'var(--teal)', fontWeight: 600, padding: '12px', marginBottom: 20 }} onClick={addRoomConfig}>
              + Add Another Room Type / Accommodation
            </button>
            <div style={{ marginTop: 14 }}>
              <div className="field"><label>Notes / Special Requests</label><textarea value={stay.notes} onChange={e => setStay(f => ({ ...f, notes: e.target.value }))} placeholder="Any special requirements..." /></div>
            </div>

            <div className="field" style={{ marginTop: 14 }}>
              <label>Billing Configuration *</label>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                {BILLING_TYPES.map(bt => (
                  <label key={bt.value} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, border: `2px solid ${stay.billingType === bt.value ? 'var(--accent)' : 'var(--border)'}`, background: stay.billingType === bt.value ? 'var(--s1)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="radio" checked={stay.billingType === bt.value} onChange={() => setStay(s => ({ ...s, billingType: bt.value }))} style={{ width: 16, height: 16 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{bt.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>Series: {bt.series} · {bt.value === 'gst' ? `${hotel?.gstRate || 5}% Tax` : '0% Tax'}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Live Bill Preview */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Bill Preview {stay.billingType === 'gst' && <span className="bill-gst-tag" style={{ marginLeft: 8 }}>GST @ {bill.gstRate}%</span>}
              </div>
              <div className="bill-box">
                <div className="bill-row"><span>Combined Room Rates × {stay.nights || 1} {stay.billingCycle === '12h' ? 'slots (12h)' : 'nights'}</span><span>{fmt(totalRoomRate * (Number(stay.nights) || 1))}</span></div>
                {stay.billingType === 'gst' && <div className="bill-row" style={{ color: 'var(--teal)' }}><span>GST @ {bill.gstRate}%</span><span>{fmt(bill.gstAmount)}</span></div>}
                <div className="bill-divider"></div>
                <div className="bill-row bill-total"><span>Total</span><span>{fmt(bill.total)}</span></div>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-ghost" onClick={() => setStep(0)}>← Back</button>
            <button className="btn btn-gold" onClick={() => {
              const errors = validateRooms();
              if (errors.length > 0) {
                onSuccess?.(errors[0], 'error');
                return;
              }
              setStep(2);
            }} disabled={stay.roomConfigs.some(c => !c.room || !c.roomRate || Number(c.roomRate) <= 0) || !stay.checkOut}>Next: ID Proof →</button>
          </div>
        </div>
      )}

      {/* STEP 2: ID PROOF */}
      {step === 2 && (
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Step 3 — ID Proof Upload</span>
            <span className="panel-sub">{guest.idType}: {guest.idNumber}</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            {existingProofs.length > 0 && (
              <div style={{ background: 'rgba(46,204,113,0.05)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: 12, padding: 18, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--success)', fontWeight: 800, fontSize: 13, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>🛡️</span> PRE-EXISTING ID DETECTED ON FILE
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {existingProofs.map((p, i) => (
                    <div key={i} className="proof-thumb" style={{ width: 100, height: 75, border: '1.5px solid var(--border)', borderRadius: 10, overflow: 'hidden', position: 'relative', cursor: 'pointer', background: '#f5f5f5' }} onClick={() => setPreviewFile({ name: p.file_name, data: p.data })}>
                       {(p.data?.startsWith('data:image/') || p.file_name?.match(/\.(jpg|jpeg|png|gif)$/i)) ? (
                         <img src={p.data} alt="Existing ID" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                       ) : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📄</div>}
                       <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 8, padding: '2px 4px', textAlign: 'center' }}>{p.type || 'ID'}</div>
                       <button className="proof-remove" onClick={(e) => { e.stopPropagation(); deleteExistingProof(p.id); }} style={{ position: 'absolute', top: 4, right: 4, opacity: 0.9 }}>×</button>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12 }}>You can proceed with the existing ID or upload a new one if the document has changed.</p>
              </div>
            )}

            <div className="proof-zone" onClick={() => fileRef.current?.click()} style={{ border: existingProofs.length > 0 ? '2px dashed var(--border)' : '2px dashed var(--gold)', opacity: existingProofs.length > 0 ? 0.7 : 1 }}>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple onChange={handleProofUpload} />
              <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
              <div style={{ fontSize: 13, color: 'var(--muted2)', fontWeight: 600 }}>{existingProofs.length > 0 ? 'Click to Update / Add Copy' : `Click to upload ${guest.idType}`}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Accepts JPG, PNG, PDF — multiple files allowed</div>
            </div>

            {proofFiles.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  🛡️ NEW SCAN READY — Will be saved to guest profile
                </div>
                <div className="proof-preview">
                  {proofFiles.map((f, i) => (
                    <div key={i} className="proof-thumb" style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setPreviewFile(f)}>
                      {f.type.startsWith('image/')
                        ? <img src={f.data} alt="ID Proof" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: 6, fontSize: 24 }}>📄</div>
                      }
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 9, padding: '2px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                      <button className="proof-remove" onClick={(e) => { e.stopPropagation(); removeProof(i); }} style={{ position: 'absolute', top: -8, right: -8, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!existingProofs.length && !proofFiles.length && (
              <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(232,184,109,0.08)', border: '1px solid rgba(232,184,109,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--muted2)' }}>
                ⚠️ Physical ID verification is mandatory for new guests. Upload a scanned copy or photo for records.
              </div>
            )}
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
            <button
              className="btn btn-gold"
              onClick={() => setStep(3)}
              disabled={existingProofs.length === 0 && proofFiles.length === 0}
            >
              {existingProofs.length > 0 && proofFiles.length === 0 ? 'Proceed with existing ID →' : proofFiles.length > 0 ? 'Next: Payment →' : 'Upload ID to Proceed'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: PAYMENT & CONFIRM */}
      {step === 3 && (
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Step 4 — Payment & Confirm</span>
            <span className="panel-sub">Amount Due: {fmt(bill.total)}</span>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                <input type="radio" checked={paymentOption === 'advance'} onChange={() => { setPaymentOption('advance'); setStay(f => ({ ...f, advancePaid: '' })); }} />
                Pay Advance
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                <input type="radio" checked={paymentOption === 'zero'} onChange={() => { setPaymentOption('zero'); setStay(f => ({ ...f, advancePaid: '0' })); }} />
                Zero Advance
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                <input type="radio" checked={paymentOption === 'full'} onChange={() => { setPaymentOption('full'); setStay(f => ({ ...f, advancePaid: String(bill.total) })); }} />
                Pay Full Amount ({fmt(bill.total)})
              </label>
            </div>

            {paymentOption === 'advance' && (
              <div className="field" style={{ marginBottom: 20 }}>
                <label>Advance Amount (₹) *</label>
                <input type="number" value={stay.advancePaid} onChange={async e => {
                  const val = Number(e.target.value) || 0;
                  if (val > bill.total) { await notify(`Advance cannot exceed Grand Total of ${fmt(bill.total)}`); return; }
                  setStay(f => ({ ...f, advancePaid: e.target.value }));
                }} placeholder="Enter advance amount" />
              </div>
            )}

            {paymentOption !== 'zero' && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Payment Method</div>
                {payments.map((p, idx) => {
                  const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type);
                  const advanceTotal = Number(stay.advancePaid) || 0;
                  const otherTotal = payments.filter(r => r.id !== p.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
                  const remaining = Math.max(0, advanceTotal - otherTotal);
                  return (
                    <div key={p.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>💳 Payment {idx + 1}</span>
                        {payments.length > 1 && (
                          <button type="button" onClick={() => setPayments(prev => prev.filter(r => r.id !== p.id))}
                            style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
                        )}
                      </div>
                      <div className="pay-type-grid">
                        {PAYMENT_TYPES.map(pt => (
                          <button key={pt.value} className={`pay-type-btn ${p.type === pt.value ? 'selected' : ''}`} onClick={() => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, type: pt.value, last5: '' } : r))}>
                            <span className="pay-type-icon">{pt.icon}</span>
                            <span className="pay-type-label">{pt.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="form-row-2" style={{ marginTop: 12 }}>
                        <div className="field">
                          <label>Amount (₹) <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>Remaining: {fmt(remaining)}</span></label>
                          <input type="number" value={p.amount} onChange={e => {
                            const val = Number(e.target.value) || 0;
                            if (val > remaining + 0.01) return;
                            setPayments(prev => prev.map(r => r.id === p.id ? { ...r, amount: e.target.value } : r));
                          }} />
                        </div>
                        {isNonCash && (
                          <div className="field">
                            <label>
                              {p.type === 'cheque' ? 'Cheque Number (Last 5 Digits)' :
                               p.type === 'bank_transfer' ? 'Transaction ID (Last 5 Digits)' :
                               'Last 5 digits (UPI ID / Card Number)'}
                            </label>
                            <input value={p.last5} onChange={e => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, last5: e.target.value.slice(-5) } : r))} placeholder="e.g. 43210" maxLength={5} />
                          </div>
                        )}
                      </div>
                      <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                        <label>Remarks <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
                        <input value={p.note} onChange={e => setPayments(prev => prev.map(r => r.id === p.id ? { ...r, note: e.target.value } : r))} placeholder="Reference / note" />
                      </div>
                    </div>
                  );
                })}
                {(() => {
                  const splitTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
                  const advanceTotal = Number(stay.advancePaid) || 0;
                  const diff = advanceTotal - splitTotal;
                  if (advanceTotal <= 0) return null;
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: Math.abs(diff) < 0.5 ? 'rgba(109,191,148,0.12)' : 'rgba(232,184,109,0.10)' }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{Math.abs(diff) < 0.5 ? '✅ Fully allocated' : `⚠️ ${fmt(diff)} not yet allocated`}</span>
                      {diff > 0.5 && (
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPayments(prev => [...prev, { ...mkPayRow(), amount: String(diff) }])}>+ Add another payment method</button>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Final Bill Summary */}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Final Confirmation
                {isCorporate && <span className="bill-gst-tag" style={{ marginLeft: 8 }}>GST Invoice</span>}
              </div>
              <div className="bill-box">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-d)' }}>{guest.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{guest.phone} · {guest.idType}: {guest.idNumber}</div>
                    {isCorporate && <div style={{ fontSize: 11, color: 'var(--teal)', marginTop: 2 }}>{guest.company} · GST: {guest.companyGst}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{stay.roomConfigs.map(c => c.room).filter(Boolean).join(', ')}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{stay.roomConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ')}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{stay.checkIn} {stay.checkInTime} → {stay.checkOut} · {stay.nights} {stay.billingCycle === '12h' ? 'Slots (12h)' : 'Nights'}</div>
                  </div>
                </div>
                <div className="bill-divider"></div>
                {(stay.billingType === 'gst' || bill.gstAmount > 0) && (
                  <div className="bill-row"><span>GST Amount ({bill.gstRate}%)</span><span>{fmt(bill.gstAmount)}</span></div>
                )}
                <div className="bill-row bill-total"><span>Net Payable</span><span>{fmt(bill.total)}</span></div>
                <div className="bill-divider"></div>
                {payments.filter(p => Number(p.amount) > 0).map(p => (
                  <div className="bill-row" key={p.id}><span>Paid Now ({p.type.replace('_', ' ')}{p.last5 ? ` ···${p.last5}` : ''})</span><span style={{ color: 'var(--success)' }}>- {fmt(Number(p.amount) || 0)}</span></div>
                ))}
                <div className="bill-row bill-due"><span>Balance Due at Checkout</span><span>{fmt(bill.balanceDue)}</span></div>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-ghost" onClick={() => setStep(2)}>← Back</button>
            <button className="btn btn-gold" onClick={handleSubmit} disabled={loading}>
              {loading ? '⏳ Checking in…' : '✅ Confirm Check-in'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
